<template>
  <v-dialog :value="value" scrollable max-width="900" @input="$emit('input', $event)">
    <v-card v-if="record" rounded="0">
      <v-toolbar color="kenyaGreen" dark flat>
        <v-toolbar-title class="font-weight-bold">
          {{ $t('memberDataSection.buyingStationOrder.bulkImport.detailsTitle') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="px-6 py-5" style="max-height: 72vh">
        <div class="d-flex align-center mb-4">
          <h3 class="font-weight-bold black--text">
            {{ $t('memberDataSection.buyingStationOrder.bulkImport.purchaseOrder') }}
          </h3>
          <v-spacer></v-spacer>
          <v-btn color="primary" class="white--text" @click="$emit('edit', record)">{{ $t('edit') }}</v-btn>
        </div>

        <div
          v-if="hasIssues"
          class="bso-detail-alert mb-5"
          :style="{ backgroundColor: themeColor('secondary2light'), borderColor: themeColor('third') }"
        >
          <p class="font-weight-bold black--text mb-1">
            {{ $t('memberDataSection.buyingStationOrder.bulkImport.issuesDetectedHeading') }}
          </p>
          <p class="text-body-2 mb-0">{{ issueDescription }}</p>
        </div>

        <v-row dense>
          <v-col v-for="field in fields" :key="field.key" cols="12" md="4" class="mb-2">
            <div class="text-body-2 text--secondary mb-1">{{ $t(field.labelKey) }}</div>
            <div class="text-body-1 font-weight-bold" :class="field.error ? 'red--text' : 'black--text'">
              {{ field.value }}
            </div>
          </v-col>
        </v-row>

        <div v-if="photoUrl" class="mt-4">
          <div class="text-body-2 text--secondary mb-1">
            {{ $t('memberDataSection.buyingStationOrder.bulkImport.photos') }}
          </div>
          <v-img :src="photoUrl" max-width="160" max-height="160" class="rounded"></v-img>
        </div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="justify-end py-4 px-6">
        <v-btn outlined color="primary" class="mr-3" min-width="120" @click="$emit('edit', record)">{{ $t('edit') }}</v-btn>
        <v-btn color="primary" class="white--text" min-width="120" @click="$emit('input', false)">
          {{ $t('memberDataSection.buyingStationOrder.bulkImport.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { SERVER_ERROR_CODE_I18N } from "@/utils/buyingStationOrderBulkImport";

const B = "memberDataSection.buyingStationOrder.bulkImport";

export default {
  name: "BuyingStationOrderBulkImportDetailDialog",
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: null },
  },
  computed: {
    rec() {
      return (this.record && this.record.recordedJsonData) || {};
    },
    md() {
      return (this.record && this.record.errorMetadata) || {};
    },
    hasIssues() {
      return Boolean(this.record && (this.record.errorCodes || []).length);
    },
    issueDescription() {
      const codes = (this.record && this.record.errorCodes) || [];
      return codes
        .map((c) => {
          const key = SERVER_ERROR_CODE_I18N[c];
          return key ? this.$t(`${B}.${key}`) : "";
        })
        .filter(Boolean)
        .join(" ");
    },
    photoUrl() {
      const pic = this.rec.coffeeCherryPic;
      return pic && (pic.file_name || pic.location) ? pic.file_name || pic.location : "";
    },
    fields() {
      const r = this.rec;
      const md = this.md;
      const has = (k) => Boolean(md[k] && md[k].length);
      const v = (val) => (val != null && String(val).trim() !== "" ? val : "—");
      const qty = r.coffeeCherryQty != null ? `${r.coffeeCherryQty} kg` : "—";
      return [
        { key: "lotId", labelKey: `${B}.lotId`, value: v(this.record.orderCode || (this.record.buyingStationOrderId ? `PO-000${this.record.buyingStationOrderId}` : null)) },
        { key: "purchaseDate", labelKey: `${B}.purchaseDate`, value: v(r.purchaseDate), error: has("purchaseDate") },
        { key: "buyingStation", labelKey: `${B}.buyingStation`, value: v(r.buyingStationName), error: has("buyingStationId") },
        { key: "farmer", labelKey: `${B}.farmer`, value: v(r.farmerName), error: has("farmerId") || has("farmerName") },
        { key: "farm", labelKey: `${B}.farm`, value: v(r.farmName), error: has("farmId") },
        { key: "plantation", labelKey: `${B}.plantation`, value: v(r.plantationName) },
        { key: "species", labelKey: `${B}.coffeeSpecies`, value: v(r.coffeeSpecies), error: has("coffeeSpecies") },
        { key: "variety", labelKey: `${B}.coffeeVariety`, value: v(r.coffeeVariety), error: has("coffeeVariety") },
        { key: "type", labelKey: `${B}.coffeeType`, value: v(r.coffeeType) },
        { key: "qty", labelKey: `${B}.coffeeCherryQuantity`, value: qty, error: has("coffeeCherryQty") },
        { key: "quality", labelKey: `${B}.coffeeCherryQuality`, value: v(r.coffeeCherryQlty), error: has("coffeeCherryQlty") },
        { key: "price", labelKey: `${B}.pricePerKg`, value: v(r.perKgPrice), error: has("perKgPrice") },
      ];
    },
  },
  methods: {
    themeColor(name) {
      const theme = this.$vuetify.theme;
      const variant = theme.dark ? "dark" : "light";
      return (theme.themes[variant] && theme.themes[variant][name]) || "";
    },
  },
};
</script>

<style lang="scss" scoped>
.bso-detail-alert {
  border: 1px solid;
  border-radius: 8px;
  padding: 16px 20px;
}
</style>
